import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  TextField,
  useTheme,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { Check as CheckIcon, Edit as EditIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import axiosInstance from "../../lib/axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Styled ListItem for professional card-like appearance
const StyledListItem = styled(ListItem)(({ theme }) => ({
  background: "#FFFFFF",
  borderRadius: theme.spacing(1),
  boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 5px 10px rgba(0,0,0,0.12)",
    transform: "translateY(-2px)",
  },
}));

const StatusTasks = () => {
  const theme = useTheme();
  const [tasks, setTasks] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [rejectingTaskId, setRejectingTaskId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Utility function to get status text from code
  const getStatusText = (statusCode) => {
    switch (statusCode) {
      case "O": return "Open";
      case "I": return "In Progress";
      case "R": return "Resolved";
      case "C": return "Closed";
      default: return "Unknown";
    }
  };

  // Utility function to get status code from text
  const getStatusCode = (statusText) => {
    switch (statusText) {
      case "Open": return "O";
      case "In Progress": return "I";
      case "Resolved": return "R";
      case "Closed": return "C";
      default: return "";
    }
  };

  // Utility function for status colors
  const getStatusColor = (status) => {
    switch (status) {
      case "Open": return "warning";
      case "In Progress": return "secondary";
      case "Resolved": return "success";
      case "Closed": return "error";
      default: return "default";
    }
  };

  useEffect(() => {
    const currentDate = new Date().toISOString().split("T")[0];
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.post(
          "TrnTicketing/GetTrnTicketDashBoard",
          {
            start: 0,
            PageSize: 0,
            SearchText: 2,
            FromDate: currentDate,
            ToDate: currentDate,
          }
        );

        if (response.data.Status === 0 && response.data.Data) {
          const fetchedTasks = response.data.Data.map((item) => {
            const ticketDate = new Date(item.TktDate);
            return {
              id: item.TicketId,
              ticketNo: item.TicketNo ? item.TicketNo.replace(/^#/, "") : "N/A",
              description: item.ServiceName || "N/A",
              categoryName: item.CategoryName || "N/A",
              subCategoryName: item.SubCategoryName || "N/A",
              serviceProvider: item.ServiceProvider || "N/A",
              propName: item.PropName || "N/A",
              raisedBy: item.Name || "Unknown",
              date: ticketDate.toISOString().split("T")[0],
              time: ticketDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              room: item.RoomNo || item.CommonAreaName || "N/A",
              status: getStatusText(item.TicketSt),
            };
          });
          setTasks(fetchedTasks);
          setTaskData(fetchedTasks.map((task) => ({
            id: task.id,
            status: task.status,
            remarks: "",
            isEditing: false,
            savedStatus: task.status,
            savedRemarks: "",
          })));
        } else {
          setError("Failed to fetch data: " + response.data.Message);
        }
      } catch (err) {
        setError("Error fetching data: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  const handleStatusChange = (id, newStatus) => {
    setTaskData((prev) =>
      prev.map((task) =>
        task.id === id ? { ...task, status: newStatus } : task
      )
    );
  };

  const handleRemarksChange = (id, newRemarks) => {
    setTaskData((prev) =>
      prev.map((task) =>
        task.id === id ? { ...task, remarks: newRemarks } : task
      )
    );
  };

  const handleEditToggle = (id) => {
    setTaskData((prev) =>
      prev.map((task) =>
        task.id === id ? { ...task, isEditing: !task.isEditing } : task
      )
    );
  };

  const handleSave = async (id) => {
    const task = taskData.find((t) => t.id === id);
    if (!task.remarks.trim()) {
      toast.error("Please provide remarks for the status update.");
      return;
    }

    try {
      const response = await axiosInstance.post(
        "TrnTicketing/TrnTicketSt",
        [{
          TicketId: id,
          TicketSt: getStatusCode(task.status),
          Flag: "",
        }],
       
      );

      console.log("Save API Response:", response.data); // Debug log
      if (response.data.Status === 0) {
        toast.success(response.data.Message || `Task ${id} status updated successfully.`);
        setTaskData((prev) =>
          prev.map((task) =>
            task.id === id
              ? {
                  ...task,
                  isEditing: false,
                  savedStatus: task.status,
                  savedRemarks: task.remarks,
                }
              : task
          )
        );
      } else {
        toast.error(response.data.Message || "Failed to update task status.");
      }
    } catch (err) {
      console.error("Save Error:", err.message);
      toast.error("Error updating task status: " + err.message);
    }
  };

  const handleCancel = (id) => {
    console.log("Cancel clicked, resetting task:", id); 
    setTaskData((prev) =>
      prev.map((task) =>
        task.id === id
          ? {
              ...task,
              isEditing: false,
              status: task.savedStatus,
              remarks: task.savedRemarks,
            }
          : task
      )
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          color: "error.main",
        }}
      >
        <Typography variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: { xs: "flex", sm: "none" },
        flexDirection: "column",
        alignItems: "center",
        p: 2,
        pb: 12,
        maxWidth: 600,
        mx: "auto",
        bgcolor: "#F9FAFB",
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F9FAFB, #E5E7EB)",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ zIndex: 9999 }}
      />
      {/* Title and Description */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontFamily: '"Roboto", sans-serif',
            fontSize: { xs: "1.5rem" },
            lineHeight: 1.4,
          }}
          component={motion.div}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          Task Status
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#6B7280",
            fontFamily: '"Roboto", sans-serif',
            fontSize: "0.9rem",
            mt: 0.5,
            lineHeight: 1.4,
          }}
        >
          Update status and remarks for your accepted tasks
        </Typography>
      </Box>

      <List sx={{ width: "100%" }}>
        {tasks.map((task, index) => {
          const currentTaskData = taskData.find((t) => t.id === task.id);
          return (
            <StyledListItem
              key={task.id}
              component={motion.div}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: { xs: "1.1rem" },
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.4,
                }}
              >
                Ticket No. {task.ticketNo}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Roboto", sans-serif',
                  color: "#1F2937",
                  mt: 0.5,
                  fontSize: "0.9rem",
                  lineHeight: 1.4,
                }}
              >
                Ticket For {task.description}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: '"Roboto", sans-serif',
                  color: "#6B7280",
                  mt: 0.5,
                  fontSize: "0.9rem",
                  lineHeight: 1.4,
                }}
              >
                Category: {task.categoryName} | SubCategory: {task.subCategoryName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: '"Roboto", sans-serif',
                  color: "#6B7280",
                  fontSize: "0.9rem",
                  lineHeight: 1.4,
                }}
              >
                Service Provider: {task.serviceProvider}
                {/* | Property: {task.propName} */}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: '"Roboto", sans-serif',
                  color: "#6B7280",
                  fontSize: "0.9rem",
                  lineHeight: 1.4,
                }}
              >
                Raised by: {task.raisedBy} | Room: {task.room}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: '"Roboto", sans-serif',
                  color: "#6B7280",
                  fontSize: "0.9rem",
                  lineHeight: 1.4,
                }}
              >
                Date: {task.date} | Time: {task.time}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mt: 1.5, width: "100%", gap: 1 }}>
                <Chip
                  label={currentTaskData.status}
                  color={getStatusColor(currentTaskData.status)}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                />
              </Box>
              <Box sx={{ mt: 1.5, width: "100%" }}>
                {currentTaskData.isEditing ? (
                  <>
                    <RadioGroup
                      row
                      value={currentTaskData.status}
                      onChange={(e) => handleStatusChange(task.id, e.target.value)}
                      aria-label={`Select status for task ${task.id}`}
                      sx={{ mb: 1, justifyContent: "flex-start" }}
                    >
                      <FormControlLabel
                        value="Resolved"
                        control={<Radio size="small" />}
                        label="Resolved"
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.95rem", color: "#111827", lineHeight: 1.4 } }}
                      />
                      <FormControlLabel
                        value="Closed"
                        control={<Radio size="small" />}
                        label="Closed"
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.95rem", color: "#111827", lineHeight: 1.4 } }}
                      />
                    </RadioGroup>
                    <TextField
                      label="Remarks"
                      value={currentTaskData.remarks}
                      onChange={(e) => handleRemarksChange(task.id, e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                      fullWidth
                      sx={{ mb: 1, bgcolor: "#FFFFFF", borderRadius: 1 }}
                      aria-label={`Remarks for task ${task.id}`}
                      InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                    />
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<CheckIcon fontSize="small" />}
                        onClick={() => handleSave(task.id)}
                        sx={{
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                          maxWidth: "100px",
                          flex: 1,
                          "&:hover": {
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          },
                        }}
                        aria-label={`Save task ${task.id}`}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleCancel(task.id)}
                        sx={{
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                          maxWidth: "100px",
                          flex: 1,
                          "&:hover": {
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          },
                        }}
                        aria-label={`Cancel edit for task ${task.id}`}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: '"Roboto", sans-serif',
                        color: "#111827",
                        fontSize: "0.95rem",
                        mt: 1,
                        lineHeight: 1.4,
                        maxWidth: "100%",
                        whiteSpace: "normal",
                      }}
                    >
                      Remarks: {currentTaskData.remarks || "None"}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => handleEditToggle(task.id)}
                        sx={{
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                          maxWidth: "100px",
                          "&:hover": {
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          },
                        }}
                        aria-label={`Edit task ${task.id}`}
                      >
                        Edit
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </StyledListItem>
          );
        })}
      </List>
    </Box>
  );
};
export default StatusTasks;